import { useEffect, lazy, Suspense } from 'react';
import { RouteErrorBoundary } from './shared/components/ErrorBoundary';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './contexts/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { SavedItemsProvider } from './contexts/SavedItemsContext';
import PrivateRoute from './components/common/PrivateRoute/PrivateRoute';
import DevAuthPanel from './components/dev/DevAuthPanel';
import AdminLoginPage from './pages/Admin/Login/AdminLoginPage';
import AdminDashboardPage from './pages/Admin/Dashboard/AdminDashboardPage';
import AdminTalentsPage from './pages/Admin/Talents/AdminTalentsPage';
import AdminTalentEmailExportPage from './pages/Admin/Talents/AdminTalentEmailExportPage';
import AdminTalentDetailPage from './pages/Admin/Talents/AdminTalentDetailPage';
import AdminDevPage from './pages/Admin/Dev/AdminDevPage';
import AdminCompaniesPage from './pages/Admin/Companies/AdminCompaniesPage';
import AdminCompanyDetailPage from './pages/Admin/Companies/AdminCompanyDetailPage';
import AdminManagedProducersPage from './pages/Admin/ManagedProducers/AdminManagedProducersPage';
import AdminManagedCastingPage from './pages/Admin/ManagedProducers/AdminManagedCastingPage';
import AdminPartnersPage from './pages/Admin/Partners/AdminPartnersPage';
import AdminPartnerDetailPage from './pages/Admin/Partners/AdminPartnerDetailPage';
import AdminPostingsPage from './pages/Admin/Postings/AdminPostingsPage';
import AdminAdsPage from './pages/Admin/Ads/AdminAdsPage';
import AdminPostingDetailPage from './pages/Admin/Postings/AdminPostingDetailPage';
import AdminPrivateRoute from './pages/Admin/components/AdminPrivateRoute';


// 공개 페이지
import LandingPage from './pages/Landing/Landing';
import LoginPage from './pages/Login/LoginPage';
import SignupPage from './pages/Signup/SignupPage';
import AuditionsPage from './pages/Auditions/AuditionsPage';
import AuditionDetailPage from './pages/AuditionDetail/AuditionDetailPage';
import TalentsPage from './pages/Talents/TalentsPage';
import TalentDetailPage from './pages/Talents/TalentDetailPage';
import TalentSlugRedirect from './pages/Talents/TalentSlugRedirect';
import CompanyDetailPage from './pages/Company/CompanyDetailPage';
import CommunityPage from './pages/Community/CommunityPage';
import AdDetailPage from './pages/Community/AdDetailPage';
import PartnersPage from './pages/Partners/PartnersPage';
import PartnerDetailPage from './pages/Partners/PartnerDetailPage';
import FaqPage from './pages/Faq/FaqPage';
import UnsubscribePage from './pages/Unsubscribe/UnsubscribePage';

// 온보딩
import TalentOnboardingPage from './pages/Onboarding/TalentOnboardingPage';
import ProducerOnboardingPage from './pages/Onboarding/ProducerOnboardingPage';

// 배우 전용
import TalentLayout from './pages/Talent/TalentLayout';
import TalentDashboardPage from './pages/Talent/Dashboard/TalentDashboardPage';
import TalentProfilePage from './pages/Talent/Profile/TalentProfilePage';
import TalentApplicationsPage from './pages/Talent/Applications/TalentApplicationsPage';
import TalentApplicationDetailPage from './pages/Talent/Applications/TalentApplicationDetailPage';
import TalentArchivePage from './pages/Talent/Archive/TalentArchivePage';
import TalentSchedulePage from './pages/Talent/Schedule/TalentSchedulePage';
import TalentSettingsPage from './pages/Talent/Settings/TalentSettingsPage';
import TalentProfileEditPage from './pages/Talent/Profile/TalentProfileEditPage';
import TalentMessagesPage from './pages/Talent/Messages/TalentMessagesPage';
import PortfolioCreatePage from './pages/Portfolio/PortfolioCreatePage';
import PortfolioPdfEditorPage from './pages/Talent/Profile/PortfolioPdfEditorPage';

// 제작사 전용
import ProducerLayout from './pages/Producer/layout/ProducerLayout';
import ProducerDashboardPage from './pages/Producer/Dashboard/ProducerDashboardPage';
import ProducerCompanyPage from './pages/Producer/Company/ProducerCompanyPage';
import ProducerProjectsPage from './pages/Producer/Projects/ProducerProjectsPage';
import ProducerCastingNewPage from './pages/Producer/Castings/ProducerCastingNewPage';
import ProducerCastingEditPage from './pages/Producer/Castings/ProducerCastingEditPage';
import ProducerBookmarksPage from './pages/Producer/Bookmarks/ProducerBookmarksPage';
import ProducerMessagesPage from './pages/Producer/Messages/ProducerMessagesPage';
import ForcePasswordChangePage from './pages/Producer/Password/ForcePasswordChangePage';
import ProducerSchedulePage from './pages/Producer/Schedule/ProducerSchedulePage';
import ProducerSettingsPage from './pages/Producer/Settings/ProducerSettingsPage';

// AI 추천 화면 미리보기(가짜 데이터) — 개발 서버 목 모드에서만 붙는다. 운영 빌드에서는
// 조건이 false로 접혀 import째 빠진다 (NODE_ENV를 먼저 보는 이유는 recommendationApi.js 참고).
const AiRecommendationsPreview = process.env.NODE_ENV !== 'production' && process.env.REACT_APP_AI_RECOMMEND_MOCK === 'true'
  ? lazy(() => import('./pages/Producer/Projects/AiRecommendations/AiRecommendationsPreview'))
  : null;

// 라우트 전환 시 항상 최상단으로 스크롤
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  const showDevAuth = process.env.NODE_ENV !== 'production';
  useEffect(() => {
    // 브라우저 자동 스크롤 복원 비활성화 (SPA에서 직접 제어)
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    const kakaoKey = process.env.REACT_APP_KAKAO_KEY;
    if (window.Kakao && kakaoKey && !window.Kakao.isInitialized()) {
      window.Kakao.init(kakaoKey);
    }
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || 'placeholder'}>
    <AdminAuthProvider>
    <AuthProvider>
    <SavedItemsProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RouteErrorBoundary>
        <Routes>
          {/* ── 공개 ─────────────────────────────────────────── */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/castings" element={<AuditionsPage />} />
          <Route path="/castings/:id" element={<AuditionDetailPage />} />
          <Route path="/talents" element={<TalentsPage />} />
          {/* 예전 AI 검색 화면 — 배우 찾기의 AI 검색 모드로 합쳤다 */}
          <Route path="/talents/ai" element={<Navigate to="/talents?ai=1" replace />} />
          <Route path="/talents/:slug" element={<TalentDetailPage />} />
          {AiRecommendationsPreview && (
            <Route path="/dev/ai-recommend" element={<Suspense fallback={null}><AiRecommendationsPreview /></Suspense>} />
          )}
          <Route path="/t/:slug" element={<TalentSlugRedirect />} />
          <Route path="/companies/:id" element={<CompanyDetailPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/partners/:slug" element={<PartnerDetailPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/community/ads/:adId" element={<AdDetailPage />} />
          <Route path="/community/:postId" element={<CommunityPage />} />
          <Route path="/faq" element={<FaqPage />} />
          {/* 알림 메일의 수신 거부 링크 — 로그인 없이 열려야 한다 */}
          <Route path="/unsubscribe" element={<UnsubscribePage />} />

          {/* ── 온보딩 (로그인 필요) ──────────────────────────── */}
          <Route element={<PrivateRoute />}>
            <Route path="/onboarding/talent" element={<TalentOnboardingPage />} />
            <Route path="/onboarding/producer" element={<ProducerOnboardingPage />} />
          </Route>

          {/* ── 배우 전용 ──────────────────────────────────────── */}
          <Route element={<PrivateRoute role="TALENT" />}>
            <Route element={<TalentLayout />}>
              <Route path="/talent/dashboard" element={<TalentDashboardPage />} />
              <Route path="/talent/profile" element={<TalentProfilePage />} />
              <Route path="/talent/applications" element={<TalentApplicationsPage />} />
              <Route path="/talent/applications/:id" element={<TalentApplicationDetailPage />} />
              <Route path="/talent/archive" element={<TalentArchivePage />} />
              <Route path="/talent/schedule" element={<TalentSchedulePage />} />
              <Route path="/talent/settings" element={<TalentSettingsPage />} />
              <Route path="/talent/messages" element={<TalentMessagesPage />} />
              <Route path="/talent/profile/edit" element={<TalentProfileEditPage />} />
            </Route>
            <Route path="/portfolio/create" element={<PortfolioCreatePage />} />
            {/* 포트폴리오 편집기는 배우 모드에서만 동작한다 */}
            <Route path="/talent/profile/pdf-editor" element={<PortfolioPdfEditorPage />} />
          </Route>

          {/* ── 제작사 전용 ────────────────────────────────────── */}
          <Route element={<PrivateRoute role="PRODUCER" />}>
            {/* 최초 비밀번호 변경 — 레이아웃 없이 단독 화면 */}
            <Route path="/producer/password" element={<ForcePasswordChangePage />} />
            <Route element={<ProducerLayout />}>
              <Route path="/producer/dashboard" element={<ProducerDashboardPage />} />
              <Route path="/producer/company" element={<ProducerCompanyPage />} />
              <Route path="/producer/projects" element={<ProducerProjectsPage />} />
              <Route path="/producer/archive" element={<ProducerBookmarksPage />} />
              <Route path="/producer/messages" element={<ProducerMessagesPage />} />
              <Route path="/producer/schedule" element={<ProducerSchedulePage />} />
              <Route path="/producer/settings" element={<ProducerSettingsPage />} />
              <Route path="/producer/castings/new" element={<ProducerCastingNewPage />} />
              <Route path="/producer/castings/edit/:id" element={<ProducerCastingEditPage />} />
            </Route>
          </Route>
          {/* ── 어드민 ─────────────────────────────────────────── */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminPrivateRoute><AdminDashboardPage /></AdminPrivateRoute>} />
          <Route path="/admin/talents" element={<AdminPrivateRoute><AdminTalentsPage /></AdminPrivateRoute>} />
          {/* :id보다 먼저 — 'emails'가 id로 잡히지 않도록 */}
          <Route path="/admin/talents/emails" element={<AdminPrivateRoute><AdminTalentEmailExportPage /></AdminPrivateRoute>} />
          <Route path="/admin/talents/:id" element={<AdminPrivateRoute><AdminTalentDetailPage /></AdminPrivateRoute>} />
          <Route path="/admin/companies" element={<AdminPrivateRoute><AdminCompaniesPage /></AdminPrivateRoute>} />
          <Route path="/admin/companies/:id" element={<AdminPrivateRoute><AdminCompanyDetailPage /></AdminPrivateRoute>} />
          <Route path="/admin/managed-producers" element={<AdminPrivateRoute><AdminManagedProducersPage /></AdminPrivateRoute>} />
          <Route path="/admin/managed-producers/:producerProfileId/casting" element={<AdminPrivateRoute><AdminManagedCastingPage /></AdminPrivateRoute>} />
          <Route path="/admin/partners" element={<AdminPrivateRoute><AdminPartnersPage /></AdminPrivateRoute>} />
          <Route path="/admin/partners/:id" element={<AdminPrivateRoute><AdminPartnerDetailPage /></AdminPrivateRoute>} />
          <Route path="/admin/postings" element={<AdminPrivateRoute><AdminPostingsPage /></AdminPrivateRoute>} />
          <Route path="/admin/postings/:id" element={<AdminPrivateRoute><AdminPostingDetailPage /></AdminPrivateRoute>} />
          <Route path="/admin/ads" element={<AdminPrivateRoute><AdminAdsPage /></AdminPrivateRoute>} />
          <Route path="/admin/dev" element={<AdminPrivateRoute><AdminDevPage /></AdminPrivateRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </RouteErrorBoundary>
        {showDevAuth && <DevAuthPanel />}
      </BrowserRouter>
    </SavedItemsProvider>
    </AuthProvider>
    </AdminAuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
